import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

// Elegant pre-curated colour pairings. The first entry is the on-boarding default.
// Each palette's full token set lives in src/index.css under [data-theme="<id>"].
export const PALETTES = [
  { id: "pinkWhite",    label: "Pink & white",  swatch: "350 55% 66%" },
  { id: "navyCream",    label: "Navy & cream",  swatch: "215 60% 24%" },
  { id: "navyGold",     label: "Navy & gold",   swatch: "215 50% 26%" },
  { id: "purpleGold",   label: "Purple & gold", swatch: "270 32% 35%" },
  { id: "sagePink",     label: "Sage & pink",   swatch: "100 14% 48%" },
  { id: "mochaCream",   label: "Mocha & cream", swatch: "22 32% 36%" },
  { id: "wineBlush",    label: "Wine & blush",  swatch: "351 50% 36%" },
  { id: "oliveLinen",   label: "Olive & linen", swatch: "64 28% 39%" },
];

// hex -> "H S% L%" string (no commas, for CSS variables consumed via hsl(var(...)))
export function hexToHslStr(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let H = 0, S = 0;
  const L = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: H = (g - b) / d + (g < b ? 6 : 0); break;
      case g: H = (b - r) / d + 2; break;
      default: H = (r - g) / d + 4;
    }
    H *= 60;
  }
  return `${Math.round(H)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}

function getLightness(hslStr) {
  if (!hslStr) return 50;
  const m = hslStr.match(/(\d+)%\s*$/);
  return m ? parseInt(m[1], 10) : 50;
}

// Pick a readable foreground (white or near-black) given a background HSL string
function readableForeground(hslStr) {
  return getLightness(hslStr) > 55 ? "0 0% 12%" : "0 0% 100%";
}

// Build derived surface colors from a custom background hex
function buildCustomVars(primaryHsl, backgroundHsl) {
  const bgL = getLightness(backgroundHsl);
  const isDark = bgL < 50;
  const fg = isDark ? "0 0% 96%" : "0 0% 10%";
  // card surface slightly different from background
  const cardL = isDark ? Math.min(bgL + 4, 22) : Math.max(bgL - 2, 92);
  const bgMatch = backgroundHsl.match(/^(\d+)\s+(\d+)%/);
  const hue = bgMatch ? bgMatch[1] : "0";
  const sat = bgMatch ? bgMatch[2] : "0";
  const card = `${hue} ${sat}% ${cardL}%`;
  const border = `${hue} ${sat}% ${isDark ? Math.min(bgL + 14, 35) : Math.max(bgL - 10, 80)}%`;
  const muted = `${hue} ${sat}% ${isDark ? Math.min(bgL + 8, 25) : Math.max(bgL - 5, 90)}%`;
  const mutedFg = isDark ? `${hue} 12% 65%` : `${hue} 10% 42%`;
  const accent = `${hue} ${Math.min(parseInt(sat || "0", 10) + 10, 60)}% ${isDark ? Math.min(bgL + 12, 28) : Math.max(bgL - 8, 88)}%`;
  return {
    "--background": backgroundHsl,
    "--foreground": fg,
    "--card": card,
    "--card-foreground": fg,
    "--popover": card,
    "--popover-foreground": fg,
    "--primary": primaryHsl,
    "--primary-foreground": readableForeground(primaryHsl),
    "--secondary": muted,
    "--secondary-foreground": fg,
    "--muted": muted,
    "--muted-foreground": mutedFg,
    "--accent": accent,
    "--accent-foreground": fg,
    "--border": border,
    "--input": border,
    "--ring": primaryHsl,
  };
}

export const PATTERNS = [
  { id: "none", label: "Plain", emoji: "○" },
  { id: "polka", label: "Polka dots", emoji: "•" },
  { id: "dots", label: "Tiny dots", emoji: "·" },
  { id: "squiggles", label: "Squiggles", emoji: "〰" },
  { id: "waves", label: "Soft waves", emoji: "≈" },
  { id: "abstract", label: "Abstract lines", emoji: "✿" },
  { id: "confetti", label: "Confetti", emoji: "✦" },
  { id: "leaves", label: "Leaves", emoji: "❀" },
  { id: "lines", label: "Diagonal lines", emoji: "╱" },
  { id: "grid", label: "Subtle grid", emoji: "▦" },
];

export function ThemeApplier() {
  const { user } = useAuth();
  useEffect(() => {
    const palette = (user && user.theme_palette) || localStorage.getItem("palette") || "pinkWhite";
    const mode = (user && user.theme_mode) || localStorage.getItem("mode") || "light";
    const customPrimary = (user && user.custom_primary_hex) || localStorage.getItem("custom_primary") || "#8B5CF6";
    const customBg =
      (user && user.custom_background_hex) ||
      localStorage.getItem("custom_background") ||
      (mode === "dark" ? "#111111" : "#FAFAFA");
    const pattern = (user && user.background_pattern) || localStorage.getItem("pattern") || "none";

    const root = document.documentElement;
    root.setAttribute("data-theme", palette);
    root.classList.toggle("dark", mode === "dark");
    document.body.setAttribute("data-pattern", pattern);

    // Clear any previous inline custom vars
    const customVarNames = [
      "--background", "--foreground", "--card", "--card-foreground",
      "--popover", "--popover-foreground", "--primary", "--primary-foreground",
      "--secondary", "--secondary-foreground", "--muted", "--muted-foreground",
      "--accent", "--accent-foreground", "--border", "--input", "--ring",
    ];
    customVarNames.forEach((v) => root.style.removeProperty(v));

    if (palette === "custom") {
      const primHsl = hexToHslStr(customPrimary);
      const bgHsl = hexToHslStr(customBg);
      if (primHsl && bgHsl) {
        const vars = buildCustomVars(primHsl, bgHsl);
        Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
      }
    }

    localStorage.setItem("palette", palette);
    localStorage.setItem("mode", mode);
    localStorage.setItem("pattern", pattern);
    if (user?.custom_primary_hex) localStorage.setItem("custom_primary", user.custom_primary_hex);
    if (user?.custom_background_hex) localStorage.setItem("custom_background", user.custom_background_hex);
  }, [user]);
  return null;
}
